import {
  ChevronDown,
  ListMusic,
  Loader2,
  Maximize2,
  Mic2,
  Minimize2,
  Music2,
  Pause,
  Play,
  Repeat,
  Repeat1,
  Shuffle,
  SkipBack,
  SkipForward,
  Video,
} from 'lucide-react';
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';

import KaraArtwork, { getKaraGradient } from '@/components/music/kara-artwork';
import KaraokeLyrics from '@/components/music/karaoke-lyrics';
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from '@/components/ui/drawer';
import Image from '@/components/ui/image';
import { useAnimeCover } from '@/hooks/use-anime-cover';
import { playerController } from '@/lib/music/player-controller';
import {
  useMusicPlayerStore,
  type LoopMode,
  type MiniPlayerTrack,
} from '@/lib/music-player-store';

type ImmersiveTab = 'player' | 'lyrics' | 'queue';

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function loopIcon(loop: LoopMode): typeof Repeat {
  return loop === 'one' ? Repeat1 : Repeat;
}

/* ─── Karaoke video overlay ──────────────────────────────────────────── */

/**
 * Muted `<video>` element loaded with the hardsub mp4. Time-syncs to
 * the engine's `currentTime` (drift > 300 ms triggers a snap) and
 * pauses/resumes with it — the engine owns audio, this view owns the
 * burnt-in lyrics visual. The wrapper caps height so the seek bar +
 * transport buttons below stay in view on portrait screens.
 */
function KaraokeVideoOverlay({
  src,
}: {
  src: string;
}): ReactElement {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isPlaying = useMusicPlayerStore((s) => s.isPlaying);
  const currentTime = useMusicPlayerStore((s) => s.currentTime);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (!Number.isFinite(currentTime) || currentTime < 0) return;
    const drift = Math.abs((video.currentTime || 0) - currentTime);
    if (drift > 0.3) video.currentTime = currentTime;
  }, [currentTime]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying && video.paused) {
      video.play().catch(() => {
        /* autoplay blocked — engine still owns audio */
      });
    } else if (!isPlaying && !video.paused) {
      video.pause();
    }
  }, [isPlaying]);

  // Track browser fullscreen state — the user can leave fullscreen via
  // ESC, the system gesture, or the OS pip widget, and the icon should
  // reflect reality not just the last click.
  useEffect(() => {
    const onFullscreenChange = (): void => {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () =>
      document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const toggleFullscreen = (): void => {
    const container = containerRef.current;
    if (!container) return;
    if (document.fullscreenElement === container) {
      document.exitFullscreen().catch(() => {
        /* ignored — exit can fail if not in fullscreen */
      });
    } else {
      container.requestFullscreen().catch(() => {
        /* ignored — permission/policy may block */
      });
    }
  };

  return (
    <div
      ref={containerRef}
      className="group relative w-full max-w-3xl"
    >
      <video
        ref={videoRef}
        src={src}
        muted
        playsInline
        preload="auto"
        className="aspect-video max-h-[45vh] w-full rounded-2xl bg-black object-contain shadow-2xl ring-1 ring-border/40"
        aria-label="Karaoke video"
      />
      <button
        type="button"
        onClick={toggleFullscreen}
        aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/40 bg-background/70 text-foreground opacity-0 backdrop-blur transition-opacity hover:bg-card focus-visible:opacity-100 group-hover:opacity-100"
      >
        {isFullscreen ? (
          <Minimize2 className="h-4 w-4" />
        ) : (
          <Maximize2 className="h-4 w-4" />
        )}
      </button>
    </div>
  );
}

/* ─── Background layer (banner + tint) ───────────────────────────────── */

/**
 * Banner background — anime banner (heavy blur) + kara-kind accent
 * overlay + theme tint so the body content reads cleanly regardless of
 * source image colors.
 */
function ImmersiveBackground({
  track,
}: {
  track: MiniPlayerTrack;
}): ReactElement {
  const cover = useAnimeCover(track.animeId, track.animeName);
  const banner = cover?.banner || cover?.poster || null;
  const accent = getKaraGradient(track.kind);

  return (
    <>
      {banner ? (
        <div aria-hidden="true" className="absolute inset-0 -z-10">
          <Image
            src={banner}
            alt=""
            fill
            sizes="100vw"
            className="object-cover scale-110 blur-2xl"
            unoptimized
          />
        </div>
      ) : null}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 opacity-40"
        style={{ background: accent }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-gradient-to-b from-background/40 via-background/75 to-background backdrop-blur-2xl"
      />
    </>
  );
}

/* ─── Mode toggle (music vs karaoke) ─────────────────────────────────── */

/**
 * Segmented Music / Karaoke toggle. Hidden when the current kara has
 * no hardsub video to switch to — there's nothing to toggle.
 */
function ModeToggle(): ReactElement | null {
  const track = useMusicPlayerStore((s) => s.currentTrack);
  const playbackMode = useMusicPlayerStore((s) => s.playbackMode);
  const setPlaybackMode = useMusicPlayerStore((s) => s.setPlaybackMode);
  if (!track?.hardsubUrl) return null;
  const isKaraoke = playbackMode === 'karaoke';
  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-background/60 p-1 backdrop-blur">
      <button
        type="button"
        onClick={() => setPlaybackMode('music')}
        aria-pressed={!isKaraoke}
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
          !isKaraoke
            ? 'bg-primary text-primary-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        <Music2 className="h-3 w-3" />
        Music
      </button>
      <button
        type="button"
        onClick={() => setPlaybackMode('karaoke')}
        aria-pressed={isKaraoke}
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
          isKaraoke
            ? 'bg-primary text-primary-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        <Video className="h-3 w-3" />
        Karaoke
      </button>
    </div>
  );
}

/* ─── Player tab body ────────────────────────────────────────────────── */

/**
 * The "now playing" hero — centerpiece (video or cover) + title block.
 * The transport controls live in a separate dock rendered by the parent
 * so they stay visible on the Lyrics and Queue tabs too.
 */
function PlayerBody({ track }: { track: MiniPlayerTrack }): ReactElement {
  const playbackMode = useMusicPlayerStore((s) => s.playbackMode);
  const showKaraokeVideo =
    playbackMode === 'karaoke' && !!track.hardsubUrl;

  return (
    <div
      className={`mx-auto flex h-full max-w-3xl flex-col items-center gap-5 px-6 py-6 ${
        showKaraokeVideo ? 'justify-start gap-4' : 'justify-center'
      }`}
    >
      <ModeToggle />

      {/* Centerpiece — video in karaoke mode, large cover in music mode. */}
      {showKaraokeVideo && track.hardsubUrl ? (
        <KaraokeVideoOverlay src={track.hardsubUrl} />
      ) : (
        <KaraArtwork
          kind={track.kind}
          animeId={track.animeId}
          animeName={track.animeName}
          className="h-60 w-60 sm:h-72 sm:w-72"
          rounded="2xl"
          badge={`${track.kind}${track.sequence ? ` ${track.sequence}` : ''}`}
          sizes="320px"
        />
      )}

      <div className="w-full max-w-md text-center">
        <h2 className="line-clamp-2 text-2xl font-bold text-foreground sm:text-3xl">
          {track.title}
        </h2>
        {track.singers && track.singers.length > 0 ? (
          <p className="mt-1.5 text-sm text-muted-foreground">
            {track.singers.slice(0, 3).join(', ')}
          </p>
        ) : null}
        {track.animeName ? (
          <p className="mt-0.5 text-xs uppercase tracking-widest text-primary/80">
            {track.animeName}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/* ─── Persistent transport dock ──────────────────────────────────────── */

/**
 * Fixed dock at the bottom of the immersive drawer. Seek bar + shuffle /
 * prev / play-pause / next / loop. Visible on every tab so the user can
 * scrub or skip without leaving the lyrics or the queue view.
 */
// eslint-disable-next-line max-lines-per-function -- transport row reads more clearly inline
function TransportDock(): ReactElement {
  const isPlaying = useMusicPlayerStore((s) => s.isPlaying);
  const isBuffering = useMusicPlayerStore((s) => s.isBuffering);
  const currentTime = useMusicPlayerStore((s) => s.currentTime);
  const duration = useMusicPlayerStore((s) => s.duration);
  const shuffle = useMusicPlayerStore((s) => s.shuffle);
  const loop = useMusicPlayerStore((s) => s.loop);
  const queueLength = useMusicPlayerStore((s) => s.queue.length);
  const toggleShuffle = useMusicPlayerStore((s) => s.toggleShuffle);
  const cycleLoop = useMusicPlayerStore((s) => s.cycleLoop);
  const LoopIcon = loopIcon(loop);
  const safeDuration = duration > 0 ? duration : 0;

  return (
    <div className="flex-shrink-0 border-t border-border/40 bg-background/70 px-4 pb-[max(env(safe-area-inset-bottom),1rem)] pt-3 backdrop-blur-md">
      <div className="mx-auto flex max-w-md flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <input
            type="range"
            min={0}
            max={safeDuration || 0}
            step={0.5}
            value={Math.min(currentTime, safeDuration || currentTime)}
            onChange={(e) =>
              playerController.seek(Number.parseFloat(e.target.value))
            }
            disabled={safeDuration <= 0}
            className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-muted/40 accent-primary"
            aria-label="Seek"
          />
          <div className="flex items-center justify-between text-[11px] tabular-nums text-muted-foreground">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(safeDuration)}</span>
          </div>
        </div>

        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={toggleShuffle}
            aria-pressed={shuffle}
            className={`inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${
              shuffle
                ? 'border-primary/40 bg-primary/15 text-primary'
                : 'border-border/70 bg-background/40 text-muted-foreground hover:bg-card hover:text-foreground'
            }`}
            aria-label="Toggle shuffle"
          >
            <Shuffle className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => playerController.previous()}
            disabled={queueLength <= 1}
            className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-border/70 bg-background/40 text-foreground transition-colors hover:bg-card disabled:opacity-40"
            aria-label="Previous"
          >
            <SkipBack className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => playerController.toggle()}
            className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/20 transition-transform hover:scale-105"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isBuffering ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : isPlaying ? (
              <Pause className="h-5 w-5" />
            ) : (
              <Play className="h-5 w-5 translate-x-[2px] fill-current" />
            )}
          </button>
          <button
            type="button"
            onClick={() => playerController.next()}
            disabled={queueLength <= 1}
            className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-border/70 bg-background/40 text-foreground transition-colors hover:bg-card disabled:opacity-40"
            aria-label="Next"
          >
            <SkipForward className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={cycleLoop}
            aria-pressed={loop !== 'off'}
            className={`inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${
              loop !== 'off'
                ? 'border-primary/40 bg-primary/15 text-primary'
                : 'border-border/70 bg-background/40 text-muted-foreground hover:bg-card hover:text-foreground'
            }`}
            aria-label={`Loop mode: ${loop}`}
          >
            <LoopIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Queue tab ──────────────────────────────────────────────────────── */

function ImmersiveQueue(): ReactElement {
  const queue = useMusicPlayerStore((s) => s.queue);
  const currentIndex = useMusicPlayerStore((s) => s.currentIndex);
  const playFromQueue = useMusicPlayerStore((s) => s.playFromQueue);

  if (queue.length === 0) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-sm text-muted-foreground">
        Queue is empty.
      </div>
    );
  }
  return (
    <div className="mx-auto w-full max-w-md px-4 py-4">
      <div className="mb-3 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold tracking-wide text-foreground">
          Up next
        </h3>
        <span className="text-[11px] uppercase tracking-widest text-muted-foreground">
          {queue.length} {queue.length === 1 ? 'track' : 'tracks'}
        </span>
      </div>
      <ol className="space-y-1.5">
        {queue.map((track, index) => {
          const isActive = currentIndex === index;
          return (
            <li key={`${track.id}-${index}`}>
              <button
                type="button"
                onClick={() => playFromQueue(index)}
                className={`flex w-full items-center gap-3 rounded-xl border p-2 text-left transition-colors ${
                  isActive
                    ? 'border-primary/40 bg-primary/10 text-primary'
                    : 'border-border/40 bg-card/40 text-foreground hover:border-border/70 hover:bg-card/70'
                }`}
              >
                <span className="w-5 shrink-0 text-center text-[11px] font-bold tabular-nums text-muted-foreground">
                  {isActive ? '▶' : index + 1}
                </span>
                <KaraArtwork
                  kind={track.kind}
                  animeId={track.animeId}
                  animeName={track.animeName}
                  className="h-10 w-10"
                />
                <span className="min-w-0 flex-1">
                  <p className="line-clamp-1 text-sm font-medium">
                    {track.title}
                  </p>
                  <p className="line-clamp-1 text-[11px] text-muted-foreground">
                    {track.kind}
                    {track.sequence ? ` ${track.sequence}` : ''}
                    {track.animeName ? ` · ${track.animeName}` : ''}
                  </p>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/* ─── Root (vaul drawer) ─────────────────────────────────────────────── */

const TABS: Array<{ key: ImmersiveTab; icon: typeof Music2; label: string }> = [
  { key: 'player', icon: Music2, label: 'Now playing' },
  { key: 'lyrics', icon: Mic2, label: 'Lyrics' },
  { key: 'queue', icon: ListMusic, label: 'Queue' },
];

/**
 * Full-screen immersive player. Wrapped in a vaul Drawer (direction:
 * bottom) so it slides up from the bottom on open and back down on
 * close — drag-to-close, focus trap, ESC handling, and body scroll
 * lock all come from vaul. Works the same on mobile and desktop.
 */
export function ImmersiveMusicPlayer(): ReactElement | null {
  const isImmersive = useMusicPlayerStore((s) => s.isImmersive);
  const closeImmersive = useMusicPlayerStore((s) => s.closeImmersive);
  const track = useMusicPlayerStore((s) => s.currentTrack);
  const [tab, setTab] = useState<ImmersiveTab>('player');

  if (!track) return null;

  // The accent color is the kara kind's primary palette tone. Exposed
  // as a CSS var so child elements can reach for it without re-running
  // the palette lookup.
  const containerStyle: CSSProperties = {
    ['--kara-accent' as never]: getKaraGradient(track.kind),
  };

  return (
    <Drawer
      open={isImmersive}
      onOpenChange={(open) => {
        if (!open) closeImmersive();
      }}
      direction="bottom"
    >
      <DrawerContent
        // Override the wrapper's baked-in `max-h-[80vh]` + `bg-background`
        // — we want full-bleed, with our themed background layer doing
        // the colour work behind transparent content. `!` keeps the
        // override winning over the wrapper's class list.
        className="!mt-0 !max-h-[100vh] h-[100vh] overflow-hidden border-0 bg-transparent !rounded-t-3xl"
      >
        <DrawerTitle className="sr-only">
          Now playing — {track.title}
        </DrawerTitle>
        <div
          // `flex-1` (not `h-full`) — vaul renders a drag-handle pill as
          // DrawerContent's first child (~24px). With `h-full` the wrapper
          // tried to be the full 100vh AND share the column with the
          // handle, clipping the bottom 24px of content under the
          // parent's `overflow-hidden`. `flex-1` takes only the remaining
          // space, no overflow.
          className="relative flex flex-1 flex-col overflow-hidden"
          style={containerStyle}
        >
          <ImmersiveBackground track={track} />

          {/* Top chrome — close button + tab pills */}
          <header className="flex items-center justify-between px-4 pb-2 pt-1">
            <button
              type="button"
              onClick={closeImmersive}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border/70 bg-background/60 text-foreground backdrop-blur transition-colors hover:bg-card/80"
              aria-label="Close immersive player"
            >
              <ChevronDown className="h-4 w-4" />
            </button>

            <div className="flex gap-1 rounded-full border border-border/70 bg-background/60 p-1 backdrop-blur">
              {TABS.map(({ key, icon: Icon, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  aria-pressed={tab === key}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                    tab === key
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon className="h-3 w-3" />
                  {label}
                </button>
              ))}
            </div>

            <div className="h-9 w-9" />
          </header>

          {/* Tab content occupies the gap between the header and the
            * dock — its own scrollbar so the dock stays anchored. */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {tab === 'player' && <PlayerBody track={track} />}
            {tab === 'lyrics' && (
              <KaraokeLyrics track={track} scale="immersive" />
            )}
            {tab === 'queue' && <ImmersiveQueue />}
          </div>

          {/* Persistent transport — same controls visible on every tab. */}
          <TransportDock />
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export default ImmersiveMusicPlayer;
